'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppToast } from '@/components/app/AppToast'
import { getPinnedPrompts } from '../_queries/pinned-queries'
import { pinPrompt, unpinPrompt, reorderPinnedPrompts } from '../_actions/pinned-actions'
import type { PromptGridItem } from '@/types/collection.types'

const PINNED_KEY = 'pinned-prompts'

/**
 * Fetch pinned prompts for a user.
 */
export function usePinnedPrompts(userId: string, initialData?: PromptGridItem[]) {
  return useQuery({
    queryKey: [PINNED_KEY, userId],
    queryFn: () => getPinnedPrompts(userId),
    initialData,
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Pin a prompt mutation with cache invalidation.
 */
export function usePinPrompt(userId: string) {
  const queryClient = useQueryClient()
  const { toast } = useAppToast()

  return useMutation({
    mutationFn: pinPrompt,
    onSuccess: (result) => {
      if (result.success) {
        toast('success', 'Prompt pinned')
        queryClient.invalidateQueries({ queryKey: [PINNED_KEY, userId] })
        queryClient.invalidateQueries({ queryKey: ['prompts-grid', userId] })
        queryClient.invalidateQueries({ queryKey: ['collections', userId] })
      } else {
        toast('error', result.error ?? 'Failed to pin prompt')
      }
    },
    onError: () => {
      toast('error', 'Failed to pin prompt')
    },
  })
}

/**
 * Unpin a prompt mutation with cache invalidation.
 */
export function useUnpinPrompt(userId: string) {
  const queryClient = useQueryClient()
  const { toast } = useAppToast()

  return useMutation({
    mutationFn: unpinPrompt,
    onSuccess: (result) => {
      if (result.success) {
        toast('success', 'Prompt unpinned')
        queryClient.invalidateQueries({ queryKey: [PINNED_KEY, userId] })
        queryClient.invalidateQueries({ queryKey: ['prompts-grid', userId] })
        queryClient.invalidateQueries({ queryKey: ['collections', userId] })
      } else {
        toast('error', result.error ?? 'Failed to unpin prompt')
      }
    },
    onError: () => {
      toast('error', 'Failed to unpin prompt')
    },
  })
}

/**
 * Reorder pinned prompts mutation.
 */
export function useReorderPinnedPrompts(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: reorderPinnedPrompts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PINNED_KEY, userId] })
    },
  })
}
